import React, { useEffect, useState } from "react";
import { Box, CircularProgress, InputAdornment, MenuItem, Select, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAllDocuments } from "../../firebase/firebaseCrud";
import { DashboardMediaCard } from "../global/DashboardMediaCard";

const SORT_OPTIONS = [
  { value: "recent", label: "Recently Added" },
  { value: "price-asc", label: "Price: Low → High" },
  { value: "price-desc", label: "Price: High → Low" },
  { value: "stock-asc", label: "Stock: Low → High" },
];

export default function AdminProductList() {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recent");
  const [page, setPage] = useState(0);
  const PER_PAGE = 10;

  useEffect(() => {
    getAllDocuments("product-list")
      .then(setAll)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleDeleted = (id) => setAll((prev) => prev.filter((p) => p.id !== id));

  const filtered = all
    .filter((p) =>
      search === "" ||
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.categories?.toString().toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      if (sort === "price-asc") return (a.price || 0) - (b.price || 0);
      if (sort === "price-desc") return (b.price || 0) - (a.price || 0);
      if (sort === "stock-asc") return (a.limit || 0) - (b.limit || 0);
      // recent: already ordered by createdAt desc from firebase
      return 0;
    });

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paged = filtered.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress sx={{ color: "#194049" }} />
      </Box>
    );
  }

  return (
    <>
      <ToastContainer />

      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          mb: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          placeholder="Search products…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          size="small"
          sx={{ flex: 1, minWidth: 180, bgcolor: "#fff", borderRadius: "8px",
            "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
        />
        <Select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          size="small"
          sx={{ fontSize: 13, minWidth: 170, bgcolor: "#fff", borderRadius: "8px",
            "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" } }}
        >
          {SORT_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
        <Typography sx={{ fontSize: 13, color: "#6b7280", ml: "auto" }}>
          {filtered.length} product{filtered.length !== 1 ? "s" : ""}
        </Typography>
      </Box>

      {/* List card */}
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "12px",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        {/* Column headers */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 2,
            py: 1,
            bgcolor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <Box sx={{ width: 56, flexShrink: 0 }} />
          <Typography sx={{ flex: 1, fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Product
          </Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", minWidth: 64, textAlign: "right" }}>
            Price
          </Typography>
          <Box sx={{ width: 72, flexShrink: 0 }} />
        </Box>

        {paged.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ color: "#9ca3af", fontSize: 14 }}>
              {search ? "No products match your search" : "No products yet"}
            </Typography>
          </Box>
        ) : (
          paged.map((item) => (
            <DashboardMediaCard
              key={item.id}
              id={item.id}
              imgSrc={item.imgSrc}
              title={item.name}
              price={item.price}
              slug={item.slug}
              stock={item.limit}
              categories={item.categories}
              description={item.description}
              onDeleted={handleDeleted}
            />
          ))
        )}

        {/* Pagination */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2,
            borderTop: "1px solid #e5e7eb",
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
            Page {page + 1} of {Math.max(totalPages, 1)}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {["Prev", "Next"].map((label) => (
              <Box
                key={label}
                onClick={() =>
                  setPage((p) =>
                    label === "Prev" ? Math.max(0, p - 1) : Math.min(totalPages - 1, p + 1)
                  )
                }
                sx={{
                  px: 2, py: 0.75, fontSize: 12, borderRadius: "6px",
                  border: "1px solid #e5e7eb", cursor: "pointer", bgcolor: "#fff", color: "#374151",
                  "&:hover": { bgcolor: "#f9fafb" },
                  opacity: (label === "Prev" && page === 0) || (label === "Next" && page >= totalPages - 1) ? 0.4 : 1,
                  pointerEvents: (label === "Prev" && page === 0) || (label === "Next" && page >= totalPages - 1) ? "none" : "auto",
                }}
              >
                {label}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </>
  );
}
